import { useEffect, useMemo, useState } from "react";
import api, { API_BASE } from "@/lib/api";
import { Link } from "react-router-dom";
import { StatusBadge, PriorityBadge, TicketIdBadge, STATUS_OPTIONS, STATUS_LABEL } from "@/components/StatusBadge";
import SLABadge from "@/components/SLABadge";
import TaskTypeBadge, { TASK_TYPE_OPTIONS, TASK_TYPE_META } from "@/components/TaskTypeBadge";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Search, Filter, Download, Users, UserPlus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/lib/i18n";

const STAFF = ["agent", "team_leader", "admin", "manager"];
const ADMIN_ROLES = ["team_leader", "admin", "manager"];

export default function Tickets() {
  const { user } = useAuth();
  const { t } = useT();
  const [tickets, setTickets] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [priority, setPriority] = useState("all");
  const [ticketType, setTicketType] = useState("all");
  const [taskType, setTaskType] = useState("all");
  const [selected, setSelected] = useState(new Set());
  const [staffUsers, setStaffUsers] = useState([]);
  const [bulkAssignee, setBulkAssignee] = useState("");
  const isStaff = user?.role !== "end_user";
  const canBulk = ADMIN_ROLES.includes(user?.role);

  const load = async () => {
    const params = {};
    if (status !== "all") params.status = status;
    if (priority !== "all") params.priority = priority;
    if (ticketType !== "all") params.ticket_type = ticketType;
    if (taskType !== "all") params.internal_task_type = taskType;
    const { data } = await api.get("/tickets", { params });
    setTickets(data);
    setSelected(new Set());
  };

  useEffect(() => { load(); }, [status, priority, ticketType, taskType]);
  useEffect(() => {
    if (canBulk) {
      api.get("/users").then((r) => setStaffUsers(r.data.filter((u) => STAFF.includes(u.role)))).catch(() => {});
    }
  }, [canBulk]);

  const filtered = useMemo(() => {
    if (!q) return tickets;
    const s = q.toLowerCase();
    return tickets.filter((t) =>
      t.title.toLowerCase().includes(s) ||
      t.ticket_number.toLowerCase().includes(s) ||
      (t.description || "").toLowerCase().includes(s) ||
      (t.organization || "").toLowerCase().includes(s) ||
      (t.assignee_name || "").toLowerCase().includes(s) ||
      (t.collaborator_names || []).join(" ").toLowerCase().includes(s)
    );
  }, [q, tickets]);

  const toggleAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map((t) => t.ticket_id)));
  };
  const toggleOne = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const runBulkAssign = async (assigneeId) => {
    if (selected.size === 0) { toast.error("Select at least one ticket"); return; }
    try {
      const { data } = await api.post("/tickets/bulk-assign", {
        ticket_ids: Array.from(selected),
        assignee_id: assigneeId || null,
      });
      toast.success(`Assigned ${data.updated} ticket(s)${data.assignee ? ` to ${data.assignee}` : " (unassigned)"}`);
      setBulkAssignee("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk assign failed");
    }
  };

  const runBulkStatus = async (newStatus) => {
    const ids = Array.from(selected);
    if (!ids.length || !newStatus) { return; }
    const notify = ["resolved", "closed"].includes(newStatus) ? " Customers will get the usual resolved email and survey." : "";
    if (!window.confirm(`Change ${ids.length} ticket(s) to ${STATUS_LABEL[newStatus] || newStatus}?${notify}`)) { return; }
    try {
      const { data } = await api.post("/tickets/bulk-status", { ticket_ids: ids, status: newStatus });
      setSelected(new Set());
      await load();
      if (data.failed?.length) {
        toast.warning(`Updated ${data.updated}, ${data.failed.length} failed: ${data.failed[0].error}`);
      } else {
        toast.success(`${data.updated} ticket(s) set to ${STATUS_LABEL[newStatus] || newStatus}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk status change failed");
    }
  };

  const runBulkDelete = async () => {
    const ids = Array.from(selected);
    if (!ids.length) { return; }
    if (!window.confirm(`Delete ${ids.length} ticket(s)? They move to Trash and can be restored for 30 days.`)) { return; }
    try {
      const { data } = await api.post("/tickets/bulk-delete", { ticket_ids: ids });
      setSelected(new Set());
      await load();
      toast.success(`Deleted ${data.deleted} ticket(s)`, {
        duration: 10000,
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              await api.post("/tickets/bulk-restore", { ticket_ids: data.ticket_ids });
              toast.success("Tickets restored");
              await load();
            } catch (e) { toast.error("Restore failed"); }
          },
        },
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk delete failed");
    }
  };

  const deleteRow = async (t) => {
    try {
      await api.delete(`/tickets/${t.ticket_id}`);
      await load();
      toast.success(`Deleted ${t.ticket_number}`, {
        duration: 10000,
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              await api.post(`/tickets/${t.ticket_id}/restore`);
              toast.success(`${t.ticket_number} restored`);
              await load();
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Restore failed");
            }
          },
        },
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const isAdmin = user?.role === "admin";

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-red-600">{t("tickets_queue")}</div>
          <h1 className="font-display text-4xl font-black tracking-tight mt-2" data-testid="tickets-heading">{t("tickets_heading")}</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isStaff && (
            <a href={`${API_BASE}/tickets/export.csv`} target="_blank" rel="noreferrer">
              <Button variant="outline" className="border-slate-200 bg-transparent hover:bg-slate-100 rounded-sm" data-testid="tickets-export-btn">
                <Download className="h-4 w-4" strokeWidth={1.5} /> {t("tickets_export_csv")}
              </Button>
            </a>
          )}
          <a href={`${API_BASE}/reports/monthly.pdf?year=${new Date().getFullYear()}&month=${new Date().getMonth() + 1}`} target="_blank" rel="noreferrer" data-testid="monthly-report-btn">
            <Button variant="outline" className="border-slate-200 bg-transparent hover:bg-slate-100 rounded-sm">
              <Download className="h-4 w-4" strokeWidth={1.5} /> {t("tickets_monthly_report")}
            </Button>
          </a>
          <Link to="/tickets/new" className="bg-red-600 hover:bg-red-500 text-white px-5 h-10 flex items-center rounded-sm text-sm font-medium" data-testid="tickets-new-btn">+ {t("new_ticket")}</Link>
        </div>
      </div>

      <div className="border border-slate-200 bg-white p-4 flex items-center gap-3 flex-wrap">
        {isStaff && (
          <div className="flex items-center gap-1 border border-slate-200 rounded-sm p-0.5" data-testid="type-tabs">
            {[
              { id: "all", label: t("tickets_type_all") },
              { id: "customer", label: t("tickets_type_customer") },
              { id: "internal", label: t("tickets_type_internal") },
            ].map((tt) => (
              <button
                key={tt.id}
                onClick={() => { setTicketType(tt.id); if (tt.id !== "internal") setTaskType("all"); }}
                data-testid={`type-tab-${tt.id}`}
                className={`px-3 h-8 text-xs font-mono uppercase tracking-[0.15em] rounded-sm transition-colors ${
                  ticketType === tt.id
                    ? "bg-red-600 text-white"
                    : "text-slate-600 hover:text-slate-900"
                }`}
              >
                {tt.label}
              </button>
            ))}
          </div>
        )}
        <div className="relative flex-1 min-w-[240px]">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" strokeWidth={1.5} />
          <Input
            data-testid="tickets-search"
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder={t("tickets_search_ph")}
            className="pl-9 bg-white border-slate-200 rounded-sm h-10 text-sm"
          />
        </div>
        <div className="flex items-center gap-2 text-slate-500">
          <Filter className="h-4 w-4" strokeWidth={1.5} />
          <span className="font-mono text-[10px] uppercase tracking-[0.2em]">{t("tickets_filter")}</span>
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-44 bg-white border-slate-200 rounded-sm h-10" data-testid="filter-status">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("tickets_all_status")}</SelectItem>
            {STATUS_OPTIONS.map((s) => (
              <SelectItem key={s} value={s}>{STATUS_LABEL[s]}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={priority} onValueChange={setPriority}>
          <SelectTrigger className="w-36 bg-white border-slate-200 rounded-sm h-10" data-testid="filter-priority">
            <SelectValue placeholder="Priority" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("tickets_all_priority")}</SelectItem>
            <SelectItem value="low">{t("tickets_priority_low")}</SelectItem>
            <SelectItem value="medium">{t("tickets_priority_medium")}</SelectItem>
            <SelectItem value="high">{t("tickets_priority_high")}</SelectItem>
            <SelectItem value="critical">{t("tickets_priority_critical")}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Task-type chip row (only visible on the Internal tab) */}
      {isStaff && ticketType === "internal" && (
        <div className="flex items-center gap-2 flex-wrap" data-testid="task-type-chips">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Task type:</span>
          <button
            onClick={() => setTaskType("all")}
            data-testid="task-type-chip-all"
            className={`px-3 h-7 text-[11px] font-mono uppercase tracking-[0.15em] rounded-sm border transition-colors ${
              taskType === "all" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
            }`}
          >All</button>
          {TASK_TYPE_OPTIONS.map((tt) => {
            const active = taskType === tt;
            const meta = TASK_TYPE_META[tt];
            const Icon = meta.icon;
            return (
              <button
                key={tt}
                onClick={() => setTaskType(tt)}
                data-testid={`task-type-chip-${tt}`}
                className={`inline-flex items-center gap-1.5 px-3 h-7 text-[11px] font-mono uppercase tracking-[0.15em] rounded-sm border transition-colors ${
                  active ? "bg-red-600 text-white border-red-600" : `${meta.tone} hover:brightness-95`
                }`}
              >
                <Icon className="h-3 w-3" strokeWidth={1.75} />
                {meta.label}
              </button>
            );
          })}
        </div>
      )}

      {/* Bulk-assign toolbar */}
      {canBulk && selected.size > 0 && (
        <div className="border border-red-500/40 bg-red-500/5 p-3 flex items-center gap-3 flex-wrap rounded-sm" data-testid="bulk-assign-bar">
          <div className="text-xs font-mono text-red-700 uppercase tracking-[0.2em]">
            <UserPlus className="inline h-3 w-3 mr-1" strokeWidth={1.5} />
            {selected.size} selected
          </div>
          <Select value={bulkAssignee} onValueChange={setBulkAssignee}>
            <SelectTrigger className="w-64 bg-white border-red-500/40 rounded-sm h-9 text-xs" data-testid="bulk-assign-select">
              <SelectValue placeholder="Choose staff to assign to…" />
            </SelectTrigger>
            <SelectContent>
              {staffUsers.map((u) => (
                <SelectItem key={u.user_id} value={u.user_id} data-testid={`bulk-assignee-${u.user_id}`}>
                  {u.name} · <span className="text-slate-500 uppercase text-[10px] ml-1">{u.role.replace("_", " ")}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            onClick={() => runBulkAssign(bulkAssignee)}
            disabled={!bulkAssignee}
            className="bg-red-600 hover:bg-red-500 rounded-sm text-xs h-9"
            data-testid="bulk-assign-apply"
          >
            Assign {selected.size}
          </Button>
          <Button
            variant="outline"
            onClick={() => runBulkAssign(null)}
            className="border-slate-200 bg-transparent hover:bg-slate-100 rounded-sm text-xs h-9"
            data-testid="bulk-unassign-apply"
          >
            Unassign
          </Button>
          <Select value="" onValueChange={runBulkStatus}>
            <SelectTrigger className="w-44 bg-white border-red-500/40 rounded-sm h-9 text-xs" data-testid="bulk-status-select">
              <SelectValue placeholder="Change status..." />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((s) => (
                <SelectItem key={s} value={s} data-testid={`bulk-status-${s}`}>{STATUS_LABEL[s]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {selected.size < filtered.length && (
            <Button variant="outline" onClick={() => setSelected(new Set(filtered.map((t) => t.ticket_id)))} className="border-slate-200 bg-transparent hover:bg-slate-100 rounded-sm text-xs h-9" data-testid="bulk-select-all-btn">
              Select all {filtered.length}
            </Button>
          )}
          <Button onClick={runBulkDelete} className="bg-red-600 hover:bg-red-500 text-white rounded-sm text-xs h-9" data-testid="bulk-delete-apply">
            <Trash2 className="h-3.5 w-3.5" strokeWidth={1.75} /> Delete {selected.size}
          </Button>
          <Button variant="ghost" onClick={() => setSelected(new Set())} className="text-slate-600 hover:text-slate-900 text-xs h-9" data-testid="bulk-clear-btn">
            Clear
          </Button>
        </div>
      )}

      <div className="border border-slate-200 bg-white">
        {canBulk && filtered.length > 0 && (
          <label className="md:hidden flex items-center gap-2 px-4 py-3 border-b border-slate-200 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
            <input type="checkbox" checked={selected.size === filtered.length} onChange={toggleAll} className="cursor-pointer accent-red-600" data-testid="bulk-select-all-mobile" />
            Select all ({filtered.length})
          </label>
        )}
        <div className="hidden md:grid grid-cols-12 gap-3 px-5 py-3 border-b border-slate-200 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 items-center">
          {canBulk && (
            <div className="col-span-1">
              <input
                type="checkbox"
                checked={filtered.length > 0 && selected.size === filtered.length}
                onChange={toggleAll}
                data-testid="bulk-select-all"
                className="cursor-pointer accent-cyan-500"
              />
            </div>
          )}
          <div className={canBulk ? "col-span-2" : "col-span-3"}>Ticket</div>
          <div className="col-span-3">Reporter / Org</div>
          <div className="col-span-2">Assignee</div>
          <div className="col-span-1">Priority</div>
          <div className="col-span-1">Status</div>
          <div className="col-span-2 text-right">SLA</div>
        </div>
        <div className="divide-y divide-slate-800">
          {filtered.length === 0 && (
            <div className="p-10 text-center text-sm text-slate-500" data-testid="tickets-empty">No tickets match your filters.</div>
          )}
          {filtered.map((t) => {
            const isSel = selected.has(t.ticket_id);
            return (
              <div
                key={t.ticket_id}
                data-testid={`ticket-row-${t.ticket_number}`}
                className={`flex flex-wrap gap-x-3 gap-y-2 md:grid md:grid-cols-12 md:gap-3 items-center px-4 md:px-5 py-3 hover:bg-slate-100 transition-colors ${isSel ? "bg-red-500/5" : ""}`}
              >
                {canBulk && (
                  <div className="col-span-1" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={isSel}
                      onChange={() => toggleOne(t.ticket_id)}
                      data-testid={`bulk-select-${t.ticket_number}`}
                      className="cursor-pointer accent-cyan-500"
                    />
                  </div>
                )}
                <Link to={`/tickets/${t.ticket_id}`} className={`flex-1 min-w-0 ${canBulk ? "col-span-2" : "col-span-3"}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <TicketIdBadge id={t.ticket_number} />
                    {t.ticket_type === "internal" && t.internal_task_type && <TaskTypeBadge type={t.internal_task_type} size="sm" />}
                  </div>
                  <div className="text-sm mt-1.5 truncate">{t.title}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5">{t.category_name || "Uncategorized"}{t.subcategory ? ` · ${t.subcategory}` : ""}</div>
                </Link>
                <Link to={`/tickets/${t.ticket_id}`} className="basis-[calc(50%-0.4rem)] md:basis-auto min-w-0 col-span-3 truncate">
                  <div className="text-sm text-slate-700 truncate">{t.reporter_name}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5 truncate">{t.organization || "—"} · {t.department || "—"}</div>
                </Link>
                <Link to={`/tickets/${t.ticket_id}`} className="basis-[calc(50%-0.4rem)] md:basis-auto min-w-0 col-span-2 truncate">
                  <div className="text-sm text-slate-700 truncate">{t.assignee_name || <span className="text-slate-500">Unassigned</span>}</div>
                  {(t.collaborators || []).length > 0 && (
                    <div className="text-[10px] text-red-600 mt-0.5 truncate inline-flex items-center gap-1" data-testid={`row-collab-count-${t.ticket_number}`}>
                      <Users className="h-2.5 w-2.5" strokeWidth={2} /> +{t.collaborators.length} shared
                    </div>
                  )}
                </Link>
                <Link to={`/tickets/${t.ticket_id}`} className="col-span-1"><PriorityBadge priority={t.priority} /></Link>
                <Link to={`/tickets/${t.ticket_id}`} className="col-span-1"><StatusBadge status={t.status} /></Link>
                <div className="ml-auto md:ml-0 col-span-2 text-right flex items-center justify-end gap-2">
                  <Link to={`/tickets/${t.ticket_id}`} className="flex-1 text-right">
                    <SLABadge
                      dueAt={t.resolution_due_at}
                      status={t.status}
                      responseDueAt={t.first_response_due_at}
                      firstResponseAt={t.first_response_at}
                      compact
                    />
                  </Link>
                  {isAdmin && (
                    <button
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); deleteRow(t); }}
                      className="p-1.5 text-slate-500 hover:text-red-400 hover:bg-red-500/10 rounded-sm transition-colors"
                      title={`Delete ${t.ticket_number}`}
                      data-testid={`delete-ticket-${t.ticket_number}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
